import { CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * Pagination — compact paginator for client-side paginated lists.
 *
 * Props:
 *   page       — current page (1-indexed)
 *   pageCount  — total page count
 *   onChange   — (page) => void
 *   testIdPrefix — optional, defaults to "pagination"
 */
export default function Pagination({ page, pageCount, onChange, testIdPrefix = "pagination" }) {
  if (pageCount <= 1) return null;

  const go = (p) => onChange(Math.max(1, Math.min(pageCount, p)));

  // Build the visible page numbers: always show 1, current ±1, last, with ellipses
  const pages = [];
  const add = (v) => { if (!pages.includes(v)) pages.push(v); };
  add(1);
  for (let p = page - 1; p <= page + 1; p++) if (p > 1 && p < pageCount) add(p);
  add(pageCount);
  const display = [];
  for (let i = 0; i < pages.length; i++) {
    display.push(pages[i]);
    if (pages[i + 1] && pages[i + 1] - pages[i] > 1) display.push("…");
  }

  return (
    <nav
      data-testid={testIdPrefix}
      className="mt-12 flex items-center justify-center gap-1.5 select-none"
      aria-label="Pagination"
    >
      <button
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        data-testid={`${testIdPrefix}-prev`}
        className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#E2E8F0] hover:border-[#C2410C] hover:text-[#C2410C] text-[#475569] transition disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Previous page"
      >
        <CaretLeft size={14} weight="bold" />
      </button>
      {display.map((p, idx) =>
        p === "…" ? (
          <span key={`e-${idx}`} className="px-2 text-[#94A3B8]">…</span>
        ) : (
          <button
            key={p}
            onClick={() => go(p)}
            data-testid={`${testIdPrefix}-page-${p}`}
            className={`inline-flex items-center justify-center min-w-10 h-10 px-3 rounded-full text-sm font-semibold transition ${
              p === page
                ? "bg-[#C2410C] text-white shadow-sm"
                : "border border-[#E2E8F0] text-[#475569] hover:border-[#C2410C] hover:text-[#C2410C]"
            }`}
            aria-current={p === page ? "page" : undefined}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => go(page + 1)}
        disabled={page >= pageCount}
        data-testid={`${testIdPrefix}-next`}
        className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#E2E8F0] hover:border-[#C2410C] hover:text-[#C2410C] text-[#475569] transition disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Next page"
      >
        <CaretRight size={14} weight="bold" />
      </button>
    </nav>
  );
}
